import * as THREE from 'three';
import { applyFow, fowUniforms } from './fowShader';
import { SURF_UNIT, surfaceTextures } from './textures';

/** Instance animation modes (aAnim.y). */
export const enum AnimMode {
  Idle = 0,
  Walk = 1,
  Attack = 2,
  Work = 3,
  Dying = 4,
  Static = 6,
}

const ANIM_GLSL_HEADER = `
attribute float part;
attribute vec3 pivot;
attribute float team;
attribute vec3 aTeam;
attribute vec4 aAnim;   // x: phase, y: mode, z: event time, w: speed (units) / height (buildings)
attribute float aBuild; // construction progress 0..1 (1 = complete)
varying vec3 vTeamMix;
varying float vBuildCut; // > 0 above the construction height: discarded
mat3 rotX(float a) { float c = cos(a), s = sin(a); return mat3(1.0,0.0,0.0, 0.0,c,s, 0.0,-s,c); }
mat3 rotZ(float a) { float c = cos(a), s = sin(a); return mat3(c,s,0.0, -s,c,0.0, 0.0,0.0,1.0); }
void animate(inout vec3 p, inout vec3 n) {
  float mode = aAnim.y;
  float t = uTime * 1.0 + aAnim.x;
  float ev = clamp((uTime - aAnim.z) / 0.45, 0.0, 1.0);
  float strike = sin(ev * 3.14159);
  int pt = int(part + 0.5);
  float ang = 0.0;
  bool rx = false;
  if (pt == 1 || pt == 2) {
    float s = pt == 1 ? 1.0 : -1.0;
    if (mode > 0.5 && mode < 1.5) { ang = sin(t * 9.0) * 0.55 * s; rx = true; }
  } else if (pt == 3) {
    if (mode > 0.5 && mode < 1.5) ang = -sin(t * 9.0) * 0.35;
    else if (mode > 1.5 && mode < 2.5) ang = -1.7 * strike;
    else if (mode > 2.5 && mode < 3.5) ang = -1.2 * max(0.0, sin(t * 4.0));
    else ang = sin(t * 1.3) * 0.04;
    rx = true;
  } else if (pt == 4) {
    if (mode > 0.5 && mode < 1.5) ang = sin(t * 9.0) * 0.35;
    else if (mode > 1.5 && mode < 2.5) ang = -0.5 * strike;
    rx = true;
  } else if (pt >= 5 && pt <= 8) {
    float ph = (pt == 5 || pt == 8) ? 0.0 : 3.14159;
    if (mode > 0.5 && mode < 1.5) ang = sin(t * 11.0 + ph) * 0.6;
    rx = true;
  } else if (pt == 11) {
    if (mode > 1.5 && mode < 2.5) ang = -1.3 * strike;
    rx = true;
  } else if (pt == 12) {
    if (mode > 0.5 && mode < 1.5) ang = -t * 4.0;
    rx = true;
  }
  if (rx && ang != 0.0) {
    mat3 r = rotX(ang);
    p = r * (p - pivot) + pivot;
    n = r * n;
  }
  if (pt == 9) {
    float k = max(p.y, 0.0);
    p.x += sin(uTime * 1.4 + aAnim.x * 3.0) * 0.025 * k;
    p.z += cos(uTime * 1.1 + aAnim.x * 2.0) * 0.015 * k;
  } else if (pt == 10) {
    // Flags: a slow wave running out from the pole, with gusts, so the cloth flaps gently.
    float d = length(p.xz - pivot.xz);
    float gust = 0.75 + 0.25 * sin(uTime * 0.7 + aAnim.x);
    p.x += (sin(uTime * 2.6 - d * 8.0 + aAnim.x) * 0.7 + sin(uTime * 4.1 - d * 13.0 + aAnim.x) * 0.3) * 0.11 * d * gust;
    p.y += sin(uTime * 2.0 - d * 6.0 + aAnim.x) * 0.02 * d;
  } else if (pt == 13) {
    mat3 r = rotZ(uTime * 1.2);
    p = r * (p - pivot) + pivot;
  }
  if (mode > 3.5 && mode < 4.5) {
    float fall = clamp((uTime - aAnim.z) / 0.7, 0.0, 1.0);
    mat3 r = rotX(-fall * fall * 1.45);
    p = r * p;
    n = r * n;
    p.y -= max(0.0, uTime - aAnim.z - 3.0) * 0.12;
  }
  // Buildings under construction are cut off cleanly at the build height (fragments above are
  // discarded) rather than squashed flat, which made overlapping faces flicker.
  vBuildCut = aBuild < 0.999 ? p.y - max(0.02, aBuild) * aAnim.w : -1.0;
}
`;

/** Point a material's sky reflection and rim at a lighting preset (see createAnimatedMaterial). */
export function setMaterialSky(material: THREE.Material, p: { skyTop: number; skyHorizon: number; ground: number }): void {
  const sky = material.userData.sky as { top: { value: THREE.Color }; horizon: { value: THREE.Color }; ground: { value: THREE.Color } } | undefined;
  if (!sky) return;
  sky.top.value.set(p.skyTop);
  sky.horizon.value.set(p.skyHorizon);
  sky.ground.value.set(p.ground);
  (material.userData.rimColor as { value: THREE.Color }).value.set(p.skyHorizon);
}

/** Linear-space vertex colours of the metal parts of unit models (steel; gold and brass trim). */
const METAL_COLOURS = [0xa8aeb8, 0xd8b040, 0xd0a020].map((h) => new THREE.Color(h));
const glslVec3 = (c: THREE.Color) => `vec3(${c.r.toFixed(4)}, ${c.g.toFixed(4)}, ${c.b.toFixed(4)})`;

export interface AnimatedMaterialOptions {
  roughness?: number;
  metalness?: number;
  surfaces?: boolean;
  /**
   * Metal parts (vertex colours matching METAL_COLOURS: blades, helmets, gilding) become glossy and
   * metallic and reflect the sky (a ground-horizon-sky gradient worked out in the shader for metal
   * pixels only: no environment map); everything else keeps its matte response.
   */
  metals?: boolean;
  /**
   * Ground contact shading: vertical faces darken towards the model's base, to `min` at the base,
   * fading out by `height` (model units). Cheap ambient occlusion where walls and trunks meet the
   * ground. Upward faces (roofs, fields, platforms) are left alone.
   */
  groundAO?: { height: number; min: number };
  /** Faint sky-coloured rim on silhouettes, so models read against the ground. */
  rim?: number;
}

/**
 * Create a lit, vertex-coloured material with GPU skeletal-style animation for
 * instanced models, fog-of-war and team colour masks. A matching depth material
 * keeps shadows consistent with the animated pose.
 *
 * Instances can also be highlighted (selected or hovered): a coloured Fresnel rim plus a little
 * tint, from the per-instance aHi attribute (see AnimatedInstances.highlight). It is part of the
 * normal lighting pass: no outline pass, no extra draw.
 */
export function createAnimatedMaterial(opts: AnimatedMaterialOptions = {}): { material: THREE.MeshStandardMaterial; depth: THREE.MeshDepthMaterial } {
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: opts.roughness ?? 0.8, metalness: opts.metalness ?? 0.05 });
  const surfaces = !!opts.surfaces;
  const metals = !!opts.metals;
  const ao = opts.groundAO;
  const rim = opts.rim ?? 0;
  const surfTex = { value: surfaces ? surfaceTextures() : null };
  const rimColor = { value: new THREE.Color(0xc8dcec) };
  const sky = { top: { value: new THREE.Color(0x4a80c8) }, horizon: { value: new THREE.Color(0xc8dcec) }, ground: { value: new THREE.Color(0x6a5a40) } };
  material.userData.rimColor = rimColor;
  material.userData.sky = sky;
  const patchVertex = (shader: THREE.WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uTime = fowUniforms.uTime;
    if (!shader.vertexShader.includes('uniform float uTime;')) shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nuniform float uTime;');
    shader.vertexShader = shader.vertexShader
      .replace('uniform float uTime;', 'uniform float uTime;\n' + ANIM_GLSL_HEADER)
      .replace('#include <beginnormal_vertex>', 'vec3 objectNormal = vec3(normal);\nvec3 animPos = vec3(position);\nanimate(animPos, objectNormal);\n#ifdef USE_TANGENT\nvec3 objectTangent = vec3(tangent.xyz);\n#endif')
      .replace('#include <begin_vertex>', 'vec3 transformed = animPos;')
      // Team parts: 1 is the player's colour itself; below 1 a darker shade of it, above 1 a lighter one
      // (toward white), so clothing can be dressed in tones of the player's colour.
      .replace('#include <color_vertex>', '#include <color_vertex>\nvColor.rgb = team > 0.2 ? (team <= 1.0 ? aTeam * team : mix(aTeam, vec3(1.0), team - 1.0)) : color.rgb;');
    // Highlight, ground shading and metal mask, passed to the fragment shader.
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec4 aHi;\nvarying vec4 vHi;\nvarying vec2 vGround;\nvarying float vMetal;')
      .replace('vec3 transformed = animPos;', `vec3 transformed = animPos;
        vHi = aHi;
        vGround = vec2(animPos.y, 1.0 - abs(normalize(objectNormal).y));
        vMetal = 0.0;
        ${metals ? METAL_COLOURS.map((c) => `vMetal = max(vMetal, 1.0 - smoothstep(0.004, 0.03, distance(color.rgb, ${glslVec3(c)})));`).join('\n') : ''}
        if (team > 0.2) vMetal = 0.0;`);
  };
  const patchLighting = (shader: THREE.WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uRimColor = rimColor;
    shader.uniforms.uSkyTop = sky.top;
    shader.uniforms.uSkyHorizon = sky.horizon;
    shader.uniforms.uSkyGround = sky.ground;
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uRimColor, uSkyTop, uSkyHorizon, uSkyGround;\nvarying vec4 vHi;\nvarying vec2 vGround;\nvarying float vMetal;')
      .replace('#include <color_fragment>', `#include <color_fragment>
        ${ao ? `diffuseColor.rgb *= mix(1.0, mix(${ao.min.toFixed(3)}, 1.0, smoothstep(0.0, ${ao.height.toFixed(3)}, vGround.x)), vGround.y);` : ''}`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>${metals ? '\nroughnessFactor = mix(roughnessFactor, 0.3, vMetal);' : ''}`)
      .replace('#include <metalnessmap_fragment>', `#include <metalnessmap_fragment>${metals ? '\nmetalnessFactor = mix(metalnessFactor, 0.85, vMetal);' : ''}`)
      // Metal reflects the sky: the reflected view direction picks a colour from the gradient.
      .replace('#include <lights_fragment_maps>', `#include <lights_fragment_maps>${metals ? `
        if (vMetal > 0.01) {
          vec3 rw = inverseTransformDirection(reflect(-normalize(vViewPosition), normal), viewMatrix);
          vec3 skyCol = mix(uSkyGround, mix(uSkyHorizon, uSkyTop, smoothstep(0.0, 0.6, rw.y)), smoothstep(-0.25, 0.05, rw.y));
          radiance += skyCol * vMetal;
        }` : ''}`)
      // Rim and highlight: only compiled in where used, and the highlight only for highlighted models.
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        if (${rim > 0 ? 'true' : 'false'} || vHi.a > 0.0) {
          float edge = 1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0);
          float edge2 = edge * edge;
          totalEmissiveRadiance += vHi.rgb * vHi.a * (0.03 + 1.4 * edge2 * edge2)${rim > 0 ? ` + uRimColor * ${rim.toFixed(3)} * edge2 * edge` : ''};
        }`);
  };
  const patch = (shader: THREE.WebGLProgramParametersWithUniforms) => {
    patchVertex(shader);
    patchLighting(shader);
    shader.fragmentShader = shader.fragmentShader.replace('void main() {', 'varying float vBuildCut;\nvoid main() {\n  if (vBuildCut > 0.0) discard;');
    if (surfaces) {
      // Surface textures (wood, brick, stone, roof tiles, thatch, canvas): one lookup, projected
      // onto the plane each face mostly lies in. Models are boxes and prisms, so this is enough.
      shader.uniforms.uSurfTex = surfTex;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float surf;\nvarying float vSurf;\nvarying vec2 vSurfUv;')
        .replace('vec3 transformed = animPos;', `vec3 transformed = animPos;
          vSurf = surf;
          vec3 surfN = abs(normal);
          vSurfUv = surfN.x > max(surfN.y, surfN.z) ? position.zy : (surfN.y > surfN.z ? position.xz : position.xy);`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2DArray uSurfTex;\nvarying float vSurf;\nvarying vec2 vSurfUv;')
        .replace('#include <color_fragment>', `#include <color_fragment>
          if (vSurf > 0.5) diffuseColor.rgb *= texture(uSurfTex, vec3(vSurfUv, floor(vSurf + 0.5) - 1.0)).r * ${(255 / SURF_UNIT).toFixed(4)};`);
    }
  };
  applyFow(material, patch);
  const key = `anim-${surfaces ? 's' : ''}${metals ? 'm' : ''}${ao ? `ao${ao.height}-${ao.min}` : ''}-r${rim}`;
  material.customProgramCacheKey = () => key;
  const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  depth.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = fowUniforms.uTime;
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nuniform float uTime;\n' + ANIM_GLSL_HEADER)
      .replace('#include <begin_vertex>', 'vec3 animPos = vec3(position);\nvec3 animN = vec3(0.0, 1.0, 0.0);\nanimate(animPos, animN);\nvec3 transformed = animPos;');
    shader.fragmentShader = shader.fragmentShader.replace('void main() {', 'varying float vBuildCut;\nvoid main() {\n  if (vBuildCut > 0.0) discard;');
  };
  depth.customProgramCacheKey = () => 'anim-depth';
  return { material, depth };
}

/**
 * An instanced mesh with per-instance animation attributes and automatic growth.
 * Instances are rewritten every frame from simulation state (count reset + push).
 */
export class AnimatedInstances {
  mesh: THREE.InstancedMesh;
  private team: THREE.InstancedBufferAttribute;
  private anim: THREE.InstancedBufferAttribute;
  private build: THREE.InstancedBufferAttribute;
  private hi: THREE.InstancedBufferAttribute;
  private capacity: number;
  count = 0;
  private static mtx = new THREE.Matrix4();
  private static q = new THREE.Quaternion();
  private static up = new THREE.Vector3(0, 1, 0);
  private static v = new THREE.Vector3();
  private static s = new THREE.Vector3();

  constructor(
    private geometry: THREE.BufferGeometry,
    private material: THREE.Material,
    private depth: THREE.Material | null,
    capacity = 64,
    castShadow = true,
  ) {
    this.capacity = capacity;
    this.mesh = this.create(capacity);
    this.mesh.castShadow = castShadow;
    this.mesh.receiveShadow = true;
    this.team = this.mesh.geometry.getAttribute('aTeam') as THREE.InstancedBufferAttribute;
    this.anim = this.mesh.geometry.getAttribute('aAnim') as THREE.InstancedBufferAttribute;
    this.build = this.mesh.geometry.getAttribute('aBuild') as THREE.InstancedBufferAttribute;
    this.hi = this.mesh.geometry.getAttribute('aHi') as THREE.InstancedBufferAttribute;
  }

  private create(cap: number): THREE.InstancedMesh {
    const g = this.geometry;
    g.setAttribute('aTeam', new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3));
    g.setAttribute('aAnim', new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4));
    g.setAttribute('aBuild', new THREE.InstancedBufferAttribute(new Float32Array(cap).fill(1), 1));
    g.setAttribute('aHi', new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4));
    const m = new THREE.InstancedMesh(g, this.material, cap);
    if (this.depth) m.customDepthMaterial = this.depth;
    m.count = 0;
    m.frustumCulled = false;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    return m;
  }

  private grow(): void {
    const old = this.mesh;
    const cap = this.capacity * 2;
    const parent = old.parent;
    const oldTeam = this.team.array as Float32Array;
    const oldAnim = this.anim.array as Float32Array;
    const oldBuild = this.build.array as Float32Array;
    const oldHi = this.hi.array as Float32Array;
    const oldMat = old.instanceMatrix.array as Float32Array;
    this.capacity = cap;
    this.mesh = this.create(cap);
    this.mesh.castShadow = old.castShadow;
    this.mesh.receiveShadow = true;
    this.team = this.mesh.geometry.getAttribute('aTeam') as THREE.InstancedBufferAttribute;
    this.anim = this.mesh.geometry.getAttribute('aAnim') as THREE.InstancedBufferAttribute;
    this.build = this.mesh.geometry.getAttribute('aBuild') as THREE.InstancedBufferAttribute;
    this.hi = this.mesh.geometry.getAttribute('aHi') as THREE.InstancedBufferAttribute;
    (this.hi.array as Float32Array).set(oldHi);
    (this.team.array as Float32Array).set(oldTeam);
    (this.anim.array as Float32Array).set(oldAnim);
    (this.build.array as Float32Array).set(oldBuild);
    (this.mesh.instanceMatrix.array as Float32Array).set(oldMat);
    if (parent) {
      parent.remove(old);
      parent.add(this.mesh);
    }
    old.dispose();
  }

  begin(): void {
    this.count = 0;
  }

  push(x: number, y: number, z: number, yaw: number, scale: number, team: THREE.Color, phase: number, mode: number, evTime: number, speed: number, build = 1): void {
    if (this.count >= this.capacity) this.grow();
    const i = this.count++;
    const S = AnimatedInstances;
    S.q.setFromAxisAngle(S.up, yaw);
    S.mtx.compose(S.v.set(x, y, z), S.q, S.s.set(scale, scale, scale));
    S.mtx.toArray(this.mesh.instanceMatrix.array, i * 16);
    const ta = this.team.array as Float32Array;
    ta[i * 3] = team.r;
    ta[i * 3 + 1] = team.g;
    ta[i * 3 + 2] = team.b;
    const aa = this.anim.array as Float32Array;
    aa[i * 4] = phase;
    aa[i * 4 + 1] = mode;
    aa[i * 4 + 2] = evTime;
    aa[i * 4 + 3] = speed;
    (this.build.array as Float32Array)[i] = build;
    (this.hi.array as Float32Array).fill(0, i * 4, i * 4 + 4);
  }

  /** Highlight the instance just pushed (selection, hover): rim colour and strength (0 to 1). */
  highlight(color: THREE.Color, strength: number): void {
    const i = this.count - 1;
    if (i < 0) return;
    const a = this.hi.array as Float32Array;
    a[i * 4] = color.r;
    a[i * 4 + 1] = color.g;
    a[i * 4 + 2] = color.b;
    a[i * 4 + 3] = strength;
  }

  end(): void {
    this.mesh.count = this.count;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.team.needsUpdate = true;
    this.anim.needsUpdate = true;
    this.build.needsUpdate = true;
    this.hi.needsUpdate = true;
  }
}
